import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, unwrap } from '../../api/client';
import { errorMessage, useNotify } from '../../lib/notify';
import { tenantKeys, type TenantDetail } from './api';
import { Callout } from './parts';

const SLUG = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[0-9][0-9 -]{5,28}$/;

/** "Ali Traders & Sons" → "ali-traders-sons". */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}

/** A readable temporary password: 3 short words of letters plus digits (12+ characters). */
export function temporaryPassword(): string {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  const body = Array.from(bytes, (b) => chars[b % chars.length]).join('');
  return `${body.slice(0, 4)}-${body.slice(4, 8)}-${body.slice(8)}`;
}

type Created = { tenant: TenantDetail; login: string; password: string };

/**
 * Opens a shop for a customer: shop name and ID, the owner's name, email or phone, and a temporary
 * password. The new shop starts on the trial; afterwards the sign-in details are shown to share.
 */
export function CreateTenantDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (tenant: TenantDetail) => void;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const [shopName, setShopName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState(temporaryPassword);
  const [mustChange, setMustChange] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [slugTaken, setSlugTaken] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);

  const errors = {
    shopName: !shopName.trim() ? 'Enter the shop name' : null,
    slug: slugTaken
      ? 'This shop ID is already taken'
      : !SLUG.test(slug)
        ? '3–63 lowercase letters, digits or hyphens'
        : null,
    ownerName: !ownerName.trim() ? 'Enter the owner’s name' : null,
    email:
      email.trim() && !EMAIL.test(email.trim())
        ? 'Enter a valid email'
        : !email.trim() && !phone.trim()
          ? 'Enter an email or a phone number'
          : null,
    phone: phone.trim() && !PHONE.test(phone.trim()) ? 'Use a number like +92 300 1234567' : null,
    password: password.length < 8 ? 'At least 8 characters' : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const show = (k: keyof typeof errors) => (submitted || k === 'slug' ? errors[k] : null);

  const reset = () => {
    setShopName('');
    setSlug('');
    setSlugEdited(false);
    setOwnerName('');
    setEmail('');
    setPhone('');
    setPassword(temporaryPassword());
    setMustChange(true);
    setSubmitted(false);
    setError(null);
    setSlugTaken(false);
    setCreated(null);
  };
  const close = () => {
    if (busy) return;
    const done = created;
    reset();
    onClose();
    if (done) onCreated(done.tenant);
  };

  const submit = () => {
    setSubmitted(true);
    if (invalid) return;
    setBusy(true);
    setError(null);
    unwrap(
      api.POST('/platform/tenants', {
        body: {
          shopName: shopName.trim(),
          shopSlug: slug,
          ownerName: ownerName.trim(),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          password,
          ownerMustChangePassword: mustChange,
        },
      }),
    )
      .then((tenant) => {
        setBusy(false);
        void qc.invalidateQueries({ queryKey: tenantKeys.all });
        notify.success(`Created ${tenant.name}`);
        setCreated({ tenant, login: email.trim() || phone.trim(), password });
      })
      .catch((e: unknown) => {
        setBusy(false);
        if (e instanceof ApiError && e.code === 'SLUG_TAKEN') setSlugTaken(true);
        else setError(e);
      });
  };

  if (created) {
    const lines = [
      `Shop ID: ${created.tenant.slug}`,
      `Login: ${created.login}`,
      `Password: ${created.password}`,
    ];
    return (
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{created.tenant.name} is ready</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Share these sign-in details with the owner.
              {mustChange && ' They choose their own password at first sign-in.'} The shop is on its
              free trial; start its paid plan from the Subscription tab when it pays.
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: 'action.hover',
                fontFamily: 'ui-monospace, monospace',
                fontSize: 14,
              }}
            >
              {lines.map((l) => (
                <div key={l}>{l}</div>
              ))}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyRounded />}
            onClick={() =>
              void navigator.clipboard
                .writeText(lines.join('\n'))
                .then(() => notify.success('Sign-in details copied'))
            }
          >
            Copy details
          </Button>
          <Button variant="contained" onClick={close}>
            Open shop
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>New tenant</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Opens a shop for a customer. It starts on the free trial from Settings, exactly like a
              shop that signs up by itself.
            </Typography>
            {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Shop name"
                value={shopName}
                onChange={(e) => {
                  setShopName(e.target.value);
                  if (!slugEdited) setSlug(slugify(e.target.value));
                  setSlugTaken(false);
                }}
                required
                fullWidth
                autoFocus
                error={Boolean(show('shopName'))}
                helperText={show('shopName') ?? ' '}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
              <TextField
                label="Shop ID"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                  setSlugEdited(true);
                  setSlugTaken(false);
                }}
                required
                fullWidth
                error={Boolean(slug && show('slug'))}
                helperText={(slug && show('slug')) || 'Used to sign in; cannot be changed later'}
                slotProps={{ htmlInput: { maxLength: 63 } }}
              />
            </Stack>
            <TextField
              label="Owner name"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              required
              error={Boolean(show('ownerName'))}
              helperText={show('ownerName') ?? ' '}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Owner email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                error={Boolean(show('email'))}
                helperText={show('email') ?? 'Email or phone: used to sign in'}
                slotProps={{ htmlInput: { inputMode: 'email', maxLength: 254 } }}
              />
              <TextField
                label="Owner phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                error={Boolean(show('phone'))}
                helperText={show('phone') ?? ' '}
                placeholder="+92 300 1234567"
                slotProps={{ htmlInput: { inputMode: 'tel', maxLength: 30 } }}
              />
            </Stack>
            <TextField
              label="Temporary password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              error={Boolean(show('password'))}
              helperText={show('password') ?? 'Shown again after the shop is created, to share.'}
              slotProps={{
                htmlInput: { maxLength: 128, autoComplete: 'off', spellCheck: false },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="New password">
                        <IconButton
                          aria-label="Generate a new password"
                          edge="end"
                          onClick={() => setPassword(temporaryPassword())}
                        >
                          <AutorenewRounded />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FormControlLabel
              control={
                <Switch checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} />
              }
              label="Owner chooses a new password at first sign-in"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={busy}>
            Create shop
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
